import React, { useState, useEffect } from 'react';
import { ArrowRight, Shield, TrendingUp, Calculator, PieChart, Target, DollarSign, CheckCircle } from 'lucide-react';
import './LandingBasic.css';
import './components/FinancialStrategy.css';

interface LandingBasicProps {
  onNavigateBack: () => void;
  onStartSimulator: () => void;
  onStartInscription: () => void;
  onOpenMonCompte: () => void;
  onStartProfilRisque: () => void;
}

const LandingBasic: React.FC<LandingBasicProps> = ({ 
  onNavigateBack, 
  onStartSimulator, 
  onStartInscription, 
  onOpenMonCompte,
  onStartProfilRisque
}) => {
  // État pour le carrousel d'images
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  // Images pour le carrousel
  const carouselImages = [
    {
      src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=90",
      alt: "Tableau de bord SOeasi Basic - Suivi simple",
      title: "Tableaux de bord simples"
    },
    {
      src: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=90",
      alt: "Graphiques de performance d'investissement",
      title: "Performance en temps réel"
    },
    {
      src: "https://images.unsplash.com/photo-1559526324-4b87b5e36e44?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=90",
      alt: "Application mobile SOeasi Basic",
      title: "Accessible partout"
    },
    {
      src: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&h=400&q=90",
      alt: "Investissement simple avec 50 euros",
      title: "Dès 50€ seulement"
    }
  ];

  // Auto-défilement du carrousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 4000); // Change toutes les 4 secondes

    return () => clearInterval(interval);
  }, [carouselImages.length]);
  return (
    <div className="hero-background">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
              <div className="service-icon-teal">
                <PieChart className="w-5 h-5 md:w-6 md:h-6 text-white" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold hero-title-gradient">
                SOeasi
              </h1>
            </div>
            <div className="flex items-center space-x-1 md:space-x-4 flex-shrink-0">
              <button 
                onClick={onNavigateBack}
                className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap"
                style={{
                  backgroundColor: '#6b7280',
                  color: 'white',
                  boxShadow: '0 0 10px rgba(107, 114, 128, 0.3)',
                  border: '1px solid rgba(107, 114, 128, 0.5)'
                }}
              >
                ← Expert
              </button>
              <button 
                onClick={onOpenMonCompte}
                className="hidden md:block px-2 md:px-4 py-1 md:py-2 text-xs md:text-base font-medium transition-all duration-300 rounded-lg hover:shadow-lg transform hover:-translate-y-0.5 whitespace-nowrap"
                style={{
                  backgroundColor: '#1e4586',
                  color: 'white',
                  boxShadow: '0 0 10px rgba(30, 69, 134, 0.3)',
                  border: '1px solid rgba(30, 69, 134, 0.5)'
                }}
              >
                Mon compte
              </button>
              <button 
                onClick={onStartInscription}
                className="hidden md:block btn-primary whitespace-nowrap"
              >
                Inscrivez-vous
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section - Version Basic */}
      <section className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-block bg-gradient-to-r from-teal-100 to-blue-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Votre premier pas vers l'indépendance financière
            </div>
            
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Investissez{' '}
              <span className="hero-title-gradient">
                sans complexité et sans Riba
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-12 leading-relaxed">
              Transformez vos 50€ en patrimoine avec SOeasi. Notre plateforme intelligente 
              vous guide pas à pas vers la croissance de vos économies, sans jargon financier compliqué en totale conformité avec vos valeurs.
            </p>

            {/* Carrousel d'images */}
            <div className="max-w-4xl mx-auto mb-12">
              <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-white">
                <div 
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
                >
                  {carouselImages.map((image, index) => (
                    <div key={index} className="min-w-full relative">
                      <img
                        src={image.src}
                        alt={image.alt}
                        className="w-full h-80 object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
                      <div className="absolute bottom-6 left-6">
                        <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2">
                          <h3 className="text-lg font-bold text-gray-900">{image.title}</h3>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Indicateurs de position */}
                <div className="absolute bottom-4 right-6 flex space-x-2">
                  {carouselImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentImageIndex 
                          ? 'bg-white shadow-lg' 
                          : 'bg-white/50 hover:bg-white/70'
                      }`}
                    />
                  ))}
                </div>

                {/* Boutons de navigation */}
                <button
                  onClick={() => setCurrentImageIndex(
                    currentImageIndex === 0 ? carouselImages.length - 1 : currentImageIndex - 1
                  )}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all duration-300"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentImageIndex(
                    currentImageIndex === carouselImages.length - 1 ? 0 : currentImageIndex + 1
                  )}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-full p-2 transition-all duration-300"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* CTA Principal */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <button
                onClick={onStartProfilRisque}
                className="group bg-gradient-to-r from-teal-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <Calculator className="w-6 h-6" />
                <span>Démarrer mon parcours</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Section Processus en 4 Étapes - Version Basic */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Bâtissez votre avenir financier,{' '}
              <span className="text-teal-600">étape par étape.</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto">
              Avec SOeasi, démarrez simplement votre parcours d'investissement. 
              Notre processus guidé vous accompagne vers l'indépendance financière.
            </p>
          </div>

          {/* Journey Simple et Lisse */}
          <div className="relative">
            {/* Fils courbés animés - Pattern en Z */}
            <div className="hidden md:block journey-curved-connection">
              {/* Connexion 1->2 - De haut (étape 1) vers bas (étape 2) */}
              <div className="curved-path curved-path-1">
                <svg viewBox="0 0 100 60">
                  <path 
                    d="M 0 10 Q 50 30 100 50" 
                    className="animated-path delay-1"
                  />
                </svg>
              </div>
              
              {/* Connexion 2->3 - De bas (étape 2) vers haut (étape 3) */}
              <div className="curved-path curved-path-2">
                <svg viewBox="0 0 100 60">
                  <path 
                    d="M 0 50 Q 50 20 100 10" 
                    className="animated-path delay-2"
                  />
                </svg>
              </div>
              
              {/* Connexion 3->4 - De haut (étape 3) vers bas (étape 4) */}
              <div className="curved-path curved-path-3">
                <svg viewBox="0 0 100 60">
                  <path 
                    d="M 0 10 Q 50 35 100 45" 
                    className="animated-path delay-3"
                  />
                </svg>
              </div>
            </div>

            {/* Étapes */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-8 relative z-10">
              
              {/* Étape 1 - Inscription Simple */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container teal-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Inscription simple sur SOeasi Basic"
                    />
                  </div>
              
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600 transition-colors duration-500">Inscription Simple</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Créez votre compte en quelques minutes. Aucune documentation complexe.
                  </p>
                </div>
              </div>

              {/* Étape 2 - Premier Investissement */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container blue-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Premier investissement avec 50 euros"
                    />
                  </div>
                
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-500">Premier Investissement</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Commencez avec seulement 50€. Découvrez les bases de l'investissement sans risque.
                  </p>
                </div>
              </div>

              {/* Étape 3 - Suivi Simple */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container emerald-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Suivi des performances d'investissement"
                    />
                  </div>
                 
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors duration-500">Suivi Simple</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Tableaux de bord clairs et simples. Suivez vos gains en temps réel.
                  </p>
                </div>
              </div>

              {/* Étape 4 - Croissance Progressive */}
              <div className="text-center group journey-step">
                <div className="relative mb-8">
                  <div className="journey-image-container indigo-hover">
                    <img 
                      src="https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=128&h=128&q=90"
                      alt="Croissance progressive de patrimoine"
                    />
                  </div>
                  
                </div>
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors duration-500">Croissance Progressive</h3>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-xs mx-auto">
                    Augmentez vos investissements à votre rythme. Construisez votre avenir sereinement.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <div className="text-center mt-12">
              <button
                onClick={onStartProfilRisque}
                className="group bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-10 py-4 rounded-2xl font-bold text-lg hover:from-teal-700 hover:to-emerald-700 transition-all duration-300 flex items-center space-x-3 shadow-xl hover:shadow-2xl transform hover:-translate-y-1 mx-auto"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 9l3 3m0 0l-3 3m3-3H8m13 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Débuter mon investissement</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Projets Section */}
      <section id="projets" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Projets d'investissement en cours
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Découvrez nos opportunités d'investissement immobilier sélectionnées avec soin 
              dans les grandes métropoles françaises.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Projet 1 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Résidence Confluence Lyon"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    🏢 Populaire
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    12.8% France
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Résidence Confluence</h3>
                <p className="text-sm text-gray-600 mb-4">📍 Lyon 2ème (69) - Rendement annuel</p>
                <p className="text-gray-700 text-sm mb-4">
                  Appartement T3 de 64m² en plein cœur du quartier Confluence, 
                  proche des transports et commerces.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="font-bold text-gray-900">50€</div>
                    <div className="text-xs text-gray-500">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">24 mois</div>
                    <div className="text-xs text-gray-500">Durée</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span className="font-semibold">87%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-teal-500 h-2 rounded-full w-[87%]"></div>
                  </div>
                </div>
                <button 
                  onClick={onStartProfilRisque}
                  className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300"
                >
                  Investir maintenant
                </button>
              </div>
            </div>

            {/* Projet 2 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Euratechnologies Park Lille"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    🌱 Éco-responsable
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    11.5% France
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Euratechnologies Park</h3>
                <p className="text-sm text-gray-600 mb-4">📍 Lille Métropole (59) - Rendement annuel</p>
                <p className="text-gray-700 text-sm mb-4">
                  Appartement T2 de 46m² dans une résidence moderne, 
                  proche du pôle technologique d'Euratechnologies.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="font-bold text-gray-900">50€</div>
                    <div className="text-xs text-gray-500">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">36 mois</div>
                    <div className="text-xs text-gray-500">Durée</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span className="font-semibold">52%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full w-[52%]"></div>
                  </div>
                </div>
                <button 
                  onClick={onStartProfilRisque}
                  className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300"
                >
                  Investir maintenant
                </button>
              </div>
            </div>

            {/* Projet 3 */}
            <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="h-48 relative overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Part-Dieu Business Lyon"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40"></div>
                <div className="absolute top-4 left-4">
                  <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-sm font-semibold">
                    ⭐ Nouveau
                  </span>
                </div>
                <div className="absolute top-4 right-4">
                  <span className="bg-green-400 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    13.2% France
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Part-Dieu Business</h3>
                <p className="text-sm text-gray-600 mb-4">📍 Lyon 3ème (69) - Rendement annuel</p>
                <p className="text-gray-700 text-sm mb-4">
                  Studio de 28m² dans le quartier d'affaires de Part-Dieu, 
                  idéal pour investissement locatif étudiant.
                </p>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-center">
                    <div className="font-bold text-gray-900">50€</div>
                    <div className="text-xs text-gray-500">Minimum</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-gray-900">18 mois</div>
                    <div className="text-xs text-gray-500">Durée</div>
                  </div>
                </div>
                <div className="mb-4">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span>Progression</span>
                    <span className="font-semibold">15%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full w-[15%]"></div>
                  </div>
                </div>
                <button 
                  onClick={onStartProfilRisque}
                  className="w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-3 rounded-lg font-semibold hover:from-teal-600 hover:to-blue-600 transition-all duration-300"
                >
                  Investir maintenant
                </button>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Plus de 25 projets d'investissement disponibles</p>
            <button 
              onClick={onStartProfilRisque}
              className="border-2 border-teal-500 text-teal-600 px-8 py-3 rounded-lg font-semibold hover:bg-teal-50 transition-all duration-300 flex items-center space-x-2 mx-auto"
            >
              <span>Voir tous les projets</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Features Section - Version simplifiée */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Pourquoi choisir SOeasi ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Une approche simple et directe pour débuter votre parcours d'investissement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="feature-icon-teal">
                <DollarSign className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Accessible dès 50€</h3>
              <p className="text-gray-600 mb-6">
                Pas besoin d'un capital important pour commencer. Investissez à votre rythme avec un minimum de 50€.
              </p>
              <div className="flex items-center text-teal-600 font-semibold">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Sans frais cachés</span>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="feature-icon-blue">
                <Shield className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">100% Sécurisé</h3>
              <p className="text-gray-600 mb-6">
                Vos investissements sont protégés par des mesures de sécurité bancaire de premier niveau.
              </p>
              <div className="flex items-center text-blue-600 font-semibold">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Régulé et conforme</span>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="feature-icon-purple">
                <Target className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Simple à utiliser</h3>
              <p className="text-gray-600 mb-6">
                Interface intuitive pensée pour les débutants. Pas besoin d'être expert pour commencer.
              </p>
              <div className="flex items-center text-purple-600 font-semibold">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>Support inclus</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section - Version Basic */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Tarification simple et transparente
            </h2>
            <p className="text-xl text-gray-600">
              Pas de surprise, pas de frais cachés. Tout est clair dès le départ.
            </p>
          </div>

          <div className="bg-gradient-to-r from-teal-50 to-blue-50 rounded-3xl p-8 md:p-12 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Offre Basic</h3>
            <div className="text-5xl font-bold text-teal-600 mb-2">Gratuit</div>
            <p className="text-gray-600 mb-8">Pour commencer</p>
            
            <ul className="text-left max-w-md mx-auto space-y-4 mb-8">
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-teal-600 mr-3" />
                <span>Investissement minimum : 50€</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-teal-600 mr-3" />
                <span>Simulateur de base</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-teal-600 mr-3" />
                <span>Support par email</span>
              </li>
              <li className="flex items-center">
                <CheckCircle className="w-5 h-5 text-teal-600 mr-3" />
                <span>Tableaux de bord simples</span>
              </li>
            </ul>
            
            <button
              onClick={onStartInscription}
              className="bg-gradient-to-r from-teal-600 to-blue-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-teal-700 hover:to-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Commencer gratuitement
            </button>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-gradient-to-r from-teal-600 to-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Prêt à commencer votre parcours d'investissement ?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Rejoignez des milliers d'utilisateurs qui ont choisi SOeasi pour débuter.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={onStartSimulator}
              className="bg-white text-teal-600 px-8 py-4 rounded-xl font-bold text-lg hover:bg-gray-100 transition-all duration-300 flex items-center justify-center space-x-3 shadow-lg"
            >
              <Calculator className="w-6 h-6" />
              <span>Faire ma simulation</span>
            </button>
          </div>
        </div>
      </section>

      {/* Footer Simple */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg flex items-center justify-center">
                <PieChart className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-2xl font-bold">SOeasi</h3>
            </div>
            <p className="text-gray-400 mb-6">
              Votre première étape vers un avenir financier serein
            </p>
            <div className="border-t border-gray-800 pt-6">
              <p className="text-gray-400">&copy; 2025 SOeasi. Tous droits réservés.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingBasic;